import React, { useEffect, useState } from "react";
import { api, apiErr } from "../lib/api";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Input, Label, Textarea } from "../components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { toast } from "sonner";
import { Send, Target, Plus, FileText, X } from "lucide-react";

const CAMP_TONE = { active: "success", closed: "outline" };
const BAND = { green: "success", amber: "warning", red: "destructive" };

export default function SupplierCampaigns() {
  const [camps, setCamps] = useState([]);
  const [ranked, setRanked] = useState([]);
  const [questionnaires, setQuestionnaires] = useState([]);
  const [openNew, setOpenNew] = useState(false);
  const [form, setForm] = useState({ name: "", cycle_period: new Date().getFullYear(), auto_target_top: 3, questionnaire_id: "", goal: "" });

  const load = () => {
    api.get("/supply-chain/campaigns").then((r) => setCamps(r.data.campaigns || []));
    api.get("/supply-chain/targeting").then((r) => setRanked(r.data.ranked || []));
    api.get("/supply-chain/questionnaires").then((r) => setQuestionnaires(r.data.questionnaires || []));
  };
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!form.name.trim()) return toast.error("Campaign name required");
    try {
      await api.post("/supply-chain/campaigns", {
        name: form.name, cycle_period: Number(form.cycle_period),
        auto_target_top: Number(form.auto_target_top), goal: form.goal || null,
        questionnaire_id: form.questionnaire_id || (questionnaires[0]?.id ?? null),
      });
      toast.success("Campaign launched");
      setOpenNew(false); setForm({ ...form, name: "", goal: "" }); load();
    } catch (e) { toast.error(apiErr(e, "Could not create campaign")); }
  };
  const close = async (id) => { try { await api.post(`/supply-chain/campaigns/${id}/close`); toast.success("Campaign closed"); load(); } catch (e) { toast.error("Failed"); } };
  const del = async (id) => { try { await api.delete(`/supply-chain/campaigns/${id}`); toast.success("Deleted"); load(); } catch (e) { toast.error("Failed"); } };

  return (
    <div className="p-6 space-y-5" data-testid="campaigns-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="label-eyebrow mb-1">Supply Chain</div>
          <h2 className="font-display text-2xl font-bold tracking-tight flex items-center gap-2"><Send className="h-6 w-6 text-accent" /> Data-Request Campaigns</h2>
          <p className="text-muted-foreground text-sm mt-1 max-w-2xl">Targeted engagement cycles. Suppliers are prioritised by an explainable Supplier Engagement Priority — spend materiality, data gaps, missing disclosures and open risk.</p>
        </div>
        <Dialog open={openNew} onOpenChange={setOpenNew}>
          <DialogTrigger asChild><Button variant="primary" data-testid="camp-new-btn"><Plus className="h-4 w-4" /> New campaign</Button></DialogTrigger>
          <DialogContent data-testid="camp-new-dialog">
            <DialogHeader><DialogTitle>New data-request campaign</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div><Label>Campaign name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="FY2026 Primary Data Drive" data-testid="camp-name-input" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Cycle period (FY)</Label><Input type="number" value={form.cycle_period} onChange={(e) => setForm({ ...form, cycle_period: e.target.value })} data-testid="camp-period-input" /></div>
                <div><Label>Auto-target top N by priority</Label><Input type="number" value={form.auto_target_top} onChange={(e) => setForm({ ...form, auto_target_top: e.target.value })} data-testid="camp-top-input" /></div>
              </div>
              <div><Label>Goal</Label><Textarea value={form.goal} onChange={(e) => setForm({ ...form, goal: e.target.value })} placeholder="Move spend-based lines onto supplier PCFs / physical activity data." data-testid="camp-goal-input" /></div>
              <div className="card-flat p-3 text-[11px] text-muted-foreground">
                Will target the top {form.auto_target_top || 0}: <span className="text-foreground">{ranked.slice(0, Number(form.auto_target_top) || 0).map((r) => r.supplier_name).join(", ") || "—"}</span>
              </div>
              <Button variant="primary" className="w-full" onClick={create} data-testid="camp-create-submit"><Send className="h-4 w-4" /> Launch campaign</Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 space-y-3">
          <div className="label-eyebrow">Campaigns</div>
          {camps.map((c) => (
            <div key={c.id} className="card-flat p-4" data-testid={`camp-${c.id}`}>
              <div className="flex items-center gap-3 flex-wrap">
                <div className="flex-1 min-w-[200px]">
                  <div className="font-display font-bold">{c.name}</div>
                  <div className="text-[10px] font-mono text-muted-foreground">FY{c.cycle_period} · {c.targets?.length || 0} supplier(s){c.questionnaire_name ? ` · ${c.questionnaire_name}` : ""}</div>
                </div>
                <Badge variant={CAMP_TONE[c.status]} className="text-[8px] uppercase">{c.status}</Badge>
                {c.status === "active" && <Button size="sm" variant="outline" onClick={() => close(c.id)} data-testid={`camp-close-${c.id}`}>Close</Button>}
                <Button size="sm" variant="ghost" onClick={() => del(c.id)} data-testid={`camp-del-${c.id}`}><X className="h-3.5 w-3.5" /></Button>
              </div>
              {c.goal && <div className="text-[11px] text-muted-foreground mt-1.5">{c.goal}</div>}
              <div className="text-[10px] text-accent mt-1">◎ {c.targeting_rationale}</div>
              <div className="flex gap-1.5 flex-wrap mt-2">
                {(c.targets || []).map((t) => <Badge key={t.supplier_id} variant="outline" className="text-[8px]">{t.supplier_name}</Badge>)}
              </div>
            </div>
          ))}
          {!camps.length && <div className="card-flat p-8 text-center text-muted-foreground text-sm">No campaigns yet. Launch one to start engaging suppliers.</div>}

          {questionnaires.map((q) => (
            <div key={q.id} className="card-flat p-4" data-testid={`questionnaire-${q.id}`}>
              <div className="label-eyebrow mb-2 flex items-center gap-2"><FileText className="h-4 w-4 text-accent" /> {q.name} <span className="font-mono text-[9px] text-muted-foreground">v{q.version}</span></div>
              <div className="flex gap-1.5 flex-wrap mb-2">{(q.standard_alignment || []).map((s) => <Badge key={s} variant="outline" className="text-[8px]">{s}</Badge>)}</div>
              <div className="space-y-1">
                {q.questions.map((qq) => (
                  <div key={qq.id} className="text-[11px] flex items-center gap-2 flex-wrap">
                    <span className="font-mono text-muted-foreground w-6">{qq.id}</span>
                    <span className="flex-1 min-w-[160px]">{qq.prompt}</span>
                    <Badge variant="outline" className="text-[7px] uppercase">{qq.category}</Badge>
                    {qq.unit && <span className="font-mono text-[9px] text-muted-foreground">{qq.unit}</span>}
                    {qq.required && <Badge variant="warning" className="text-[7px] uppercase">required</Badge>}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        <div className="space-y-3">
          <div className="label-eyebrow flex items-center gap-2"><Target className="h-4 w-4 text-accent" /> Engagement priority</div>
          {ranked.map((r) => (
            <div key={r.supplier_id} className="card-flat p-3" data-testid={`priority-${r.supplier_id}`}>
              <div className="flex items-center gap-2">
                <div className="font-mono text-lg font-bold w-10">{r.priority_score}</div>
                <div className="flex-1 min-w-0"><div className="text-xs font-medium truncate">{r.supplier_name}</div></div>
                <Badge variant={BAND[r.band]} className="text-[8px] uppercase">{r.band}</Badge>
              </div>
              <div className="text-[10px] text-muted-foreground mt-1">{r.why.join(" · ")}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
